import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Video, Eye, MousePointerClick, IndianRupee, BadgePercent, Wallet, Loader2, Plus, Gift, Share2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fmtINR, fmtDateTime } from "../../lib/api";
import { PageHeader, StatCard, LoadingRows } from "../../components/Shared";
import { Button } from "../../components/ui/button";
import { inr2 } from "./CreatorMonetization";
import MonetizationCelebration from "./MonetizationCelebration";

export default function CreatorDashboard() {
  const [me, setMe] = useState(null);
  const [celebrate, setCelebrate] = useState(false);
  const [verStatus, setVerStatus] = useState(null);
  useEffect(() => {
    api.get("/vid/creator/me").then((r) => {
      setMe(r.data);
      if (r.data.monetization?.approved && !r.data.monetization?.celebrated) setCelebrate(true);
    }).catch((e) => toast.error(errMsg(e)));
    api.get("/vid/creator/verify/status").then((r) => setVerStatus(r.data)).catch(() => {});
  }, []);
  const closeCelebration = async () => {
    setCelebrate(false);
    try { await api.post("/vid/creator/monetization/celebrated"); } catch { /* ignore */ }
  };
  if (!me) return <LoadingRows rows={4} />;
  const e = me.earnings;
  const unverified = verStatus ? !verStatus.verified : false;

  return (
    <div data-testid="creator-dashboard">
      {unverified && (
        <div className="mb-6 rounded-2xl border border-amber-500/40 bg-amber-500/10 p-4 flex flex-wrap items-center gap-3" data-testid="verify-notice">
          <ShieldAlert className="h-5 w-5 text-amber-500 shrink-0" />
          <p className="text-sm font-semibold flex-1 min-w-[220px]">
            Verify your email to unlock referral rewards and withdrawals.
            {verStatus.deadline && <span className="block text-xs text-muted-foreground font-medium">Deadline: {fmtDateTime(verStatus.deadline)}</span>}
          </p>
          <Link to="/creator/verify" data-testid="verify-notice-btn"
            className="h-9 px-5 rounded-full text-[13px] font-extrabold text-white inline-flex items-center gradient-bg">Verify Now</Link>
        </div>
      )}
      {celebrate && <MonetizationCelebration onClose={closeCelebration} />}
      <PageHeader title="Creator Studio" subtitle={`@${me.creator.slug} · Free Creator account`} testid="creator-title">
        <Link to="/creator/videos"><Button data-testid="creator-add-video-cta" className="rounded-full font-bold gradient-bg text-white border-0 glow-sm"><Plus className="h-4 w-4 mr-1.5" />Add Video</Button></Link>
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={Video} label="Videos" value={me.stats.videos} testid="cr-stat-videos" tint="bg-violet-500/10 text-violet-500" />
        <StatCard icon={Eye} label="Total Views" value={me.stats.views} testid="cr-stat-views" delay={0.05} tint="bg-blue-500/10 text-blue-500" />
        <StatCard icon={MousePointerClick} label="Affiliate Clicks" value={me.stats.clicks} testid="cr-stat-clicks" delay={0.1} tint="bg-amber-500/10 text-amber-500" />
        <StatCard icon={IndianRupee} label="Total Earnings" value={fmtINR(e.total)} testid="cr-stat-earnings" delay={0.15} tint="bg-emerald-500/10 text-emerald-500" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        {[["Pending", e.pending, "linear-gradient(135deg,#d97706,#f59e0b)"], ["Approved (withdrawable)", e.approved, "linear-gradient(135deg,#2563EB,#06B6D4)"], ["Paid Out", e.paid, "linear-gradient(135deg,#7C3AED,#DB2777)"]].map(([l, val, bg], i) => (
          <motion.div key={l} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
            data-testid={`cr-earn-${l.split(" ")[0].toLowerCase()}`} className="rounded-2xl p-5 text-white" style={{ background: bg }}>
            <p className="text-[11px] font-bold uppercase tracking-wider text-white/75">{l}</p>
            <p className="font-display font-extrabold text-2xl mt-1.5">{fmtINR(val)}</p>
          </motion.div>
        ))}
      </div>

      <div className="mb-8" data-testid="cr-monetization-summary">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display font-bold text-lg">View Monetization</h2>
          <Link to="/creator/monetization" data-testid="cr-goto-monetization" className="text-xs font-bold text-primary hover:underline">Full report →</Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[["Eligible Views", me.monetization?.eligible_views ?? 0, "linear-gradient(135deg,#007A4D,#059669)"],
            ["Invalid/Fraud Views", me.monetization?.invalid_views ?? 0, "linear-gradient(135deg,#be123c,#f43f5e)"],
            ["Monetized Videos", me.monetization?.monetized_videos ?? 0, "linear-gradient(135deg,#0b4a8f,#2563EB)"],
            ["View Earnings", inr2((me.monetization?.earn_pending ?? 0) + (me.monetization?.earn_approved ?? 0) + (me.monetization?.earn_paid ?? 0)), "linear-gradient(135deg,#7C3AED,#DB2777)"]].map(([l, val, bg], i) => (
            <motion.div key={l} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              data-testid={`cr-mon-${l.split(" ")[0].toLowerCase()}`} className="rounded-2xl p-4 text-white" style={{ background: bg }}>
              <p className="text-[10px] font-bold uppercase tracking-wider text-white/75">{l}</p>
              <p className="font-display font-extrabold text-xl mt-1">{val}</p>
            </motion.div>
          ))}
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        {[
          { to: "/creator/videos", icon: Video, t: "My Videos", d: "Add YouTube / Instagram / Facebook video links", testid: "cr-goto-videos" },
          { to: "/creator/monetization", icon: IndianRupee, t: "Monetization", d: "View-based earnings per 1,000 eligible views", testid: "cr-goto-monetize" },
          { to: "/creator/referrals", icon: Gift, t: "Refer & Earn", d: `Invite creators — earn ₹${me.referral?.reward ?? 5} per qualified referral (${me.referral?.invited ?? 0} invited)`, testid: "cr-goto-referrals" },
          { to: "/creator/site-affiliate", icon: Share2, t: "Website & Store Affiliate", d: "Refer businesses — earn commission per approved order", testid: "cr-goto-siteaff" },
          { to: "/creator/offers", icon: BadgePercent, t: "Affiliate Products & Services", d: "Browse offers & get your tracking links", testid: "cr-goto-offers" },
          { to: "/creator/earnings", icon: Wallet, t: "Earnings & Withdraw", d: "Commissions, payouts & withdrawals", testid: "cr-goto-earnings" },
        ].map((c, i) => (
          <motion.div key={c.to} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}>
            <Link to={c.to} data-testid={c.testid} className="block bg-card border border-border rounded-2xl p-6 hover:shadow-xl hover:-translate-y-1 transition-all h-full">
              <div className="h-11 w-11 rounded-xl flex items-center justify-center mb-4" style={{ background: "linear-gradient(135deg,#007A4D,#2563EB)" }}>
                <c.icon className="h-5 w-5 text-white" />
              </div>
              <p className="font-display font-bold">{c.t}</p>
              <p className="text-xs text-muted-foreground mt-1">{c.d}</p>
            </Link>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
